import type { Combo, Side, Stuff } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ComboGlyphs } from "./ComboBits.tsx";
import { GrenadeGlyph } from "./GrenadeGlyph.tsx";

export type Pick = { kind: "single"; stuff: Stuff } | { kind: "combo"; combo: Combo };

/** Searchable list of the map's singles (and combos), to add to a combo, execution or post-plant. */
export function StuffPicker({
  stuffs,
  combos = [],
  side,
  exclude = new Set<string>(),
  onPick,
  disabled,
}: {
  stuffs: Stuff[];
  combos?: Combo[];
  /** Shown first; the other side stays one click away. */
  side?: Side;
  exclude?: Set<string>;
  onPick: (p: Pick) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [otherSide, setOtherSide] = useState(false);
  const match = (title: string, s: Side) =>
    title.toLowerCase().includes(q.trim().toLowerCase()) && (!side || otherSide || s === side);
  const singles = stuffs.filter((s) => !exclude.has(s.id) && match(s.title, s.side));
  const combosShown = combos.filter((c) => !exclude.has(c.id) && match(c.title, c.side));
  const none = singles.length === 0 && combosShown.length === 0;

  return (
    <div className="picker">
      <div className="picker-head">
        <input
          type="search"
          className="search-chip"
          placeholder={t("picker2.search")}
          aria-label={t("picker2.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {side && (
          <label className="check small">
            <input type="checkbox" checked={otherSide} onChange={(e) => setOtherSide(e.target.checked)} />
            {t("picker2.otherSide")}
          </label>
        )}
      </div>
      {none ? (
        <p className="muted small">{stuffs.length + combos.length === 0 ? t("picker2.emptyMap") : t("picker2.noMatch")}</p>
      ) : (
        <ul className="picker-list">
          {combosShown.map((c) => (
            <li key={c.id}>
              <button type="button" className="picker-item" disabled={disabled} onClick={() => onPick({ kind: "combo", combo: c })}>
                <ComboGlyphs combo={c} size={10} />
                <span className="picker-title">{c.title}</span>
                <span className="muted small">
                  {t("combo.kind")} · {t(`sideShort.${c.side}`)}
                </span>
                <span className="picker-add">{t("picker2.add")}</span>
              </button>
            </li>
          ))}
          {singles.map((s) => (
            <li key={s.id}>
              <button type="button" className="picker-item" disabled={disabled} onClick={() => onPick({ kind: "single", stuff: s })}>
                <GrenadeGlyph grenade={s.grenade} size={12} />
                <span className="picker-title">{s.title}</span>
                <span className="muted small">
                  {t(`areaShort.${s.area}`)} · {t(`sideShort.${s.side}`)}
                </span>
                <span className="picker-add">{t("picker2.add")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Up, down and remove buttons for a row of an ordered list. */
export function RowMoves({ index, count, onMove, onRemove, label }: { index: number; count: number; onMove: (from: number, to: number) => void; onRemove: () => void; label: string }) {
  const { t } = useTranslation();
  return (
    <span className="row-moves">
      <button type="button" className="btn btn-quiet btn-icon" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label={t("picker2.up", { name: label })}>
        ↑
      </button>
      <button
        type="button"
        className="btn btn-quiet btn-icon"
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
        aria-label={t("picker2.down", { name: label })}
      >
        ↓
      </button>
      <button type="button" className="btn btn-quiet btn-icon btn-danger" onClick={onRemove} aria-label={t("picker2.remove", { name: label })}>
        ×
      </button>
    </span>
  );
}

export function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
